import { createChart } from "lightweight-charts";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Select, Spin, Tooltip } from "antd";
import {
  FullscreenOutlined,
  FullscreenExitOutlined,
  InfoCircleOutlined,
  BulbOutlined,
  BulbFilled,
} from "@ant-design/icons";
import { FormatUSD, formatDateInTimeZone } from "../../../utils/helpers/string";
import { getProfitColor } from "./TradeDetailsCard";

// CandleStickChart component
const CandleStickChart = ({
  symbol = null,
  startDate = "",
  endDate = "",
  color = "#26a69a",
  tradeData,
  handleThemeToggle,
  theme, // 'dark' or 'light'
  setMaeMfedetails,
}) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();
  const { idToken } = useSelector((state) => state.auth);
  const [timeFrame, setTimeFrame] = useState("five_minute");
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFullScreen, setFullScreen] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const getTradeSideIcon = (side) => {
    return side === "BUY" || side === 1
      ? "long"
      : side === "SELL" || side === 2
      ? "short"
      : "";
  };
  // Memoize request payload to avoid re-creating on unrelated renders
  const requestPayload = useMemo(() => {
    const openRaw = tradeData?.open_time || tradeData?.enteredAt || null;
    const closeRaw = tradeData?.close_time || tradeData?.exitedAt || null;
    return {
      open_time:
        typeof openRaw === "string" ? openRaw.replace("Z", "") : openRaw,
      close_time:
        typeof closeRaw === "string" ? closeRaw.replace("Z", "") : closeRaw,
      open_price: tradeData?.open_price ?? null,
      close_price: tradeData?.close_price ?? null,
      direction: getTradeSideIcon(tradeData?.trade_side) || "long",
      start_date: startDate,
      end_date: endDate,
      timeline: timeFrame,
    };
  }, [
    tradeData?.open_time,
    tradeData?.enteredAt,
    tradeData?.close_time,
    tradeData?.exitedAt,
    tradeData?.open_price,
    tradeData?.close_price,
    tradeData?.direction,
    startDate,
    endDate,
    timeFrame,
  ]);

  // Track last request to prevent duplicate calls
  const lastRequestKeyRef = useRef(null);

  // Fetch chart data from API
  useEffect(() => {
    if (!symbol || !startDate || !endDate) return;
    setError(null);
    const config = {
      headers: { Authorization: `Bearer ${idToken}` },
    };

    const requestKey = JSON.stringify({ symbol, payload: requestPayload });
    if (lastRequestKeyRef.current === requestKey) return;
    lastRequestKeyRef.current = requestKey;

    setChartLoading(true);

    axios
      .post(
        `https://tickdata.alpha-futures.com/api/symbol/${symbol}`,
        requestPayload,
        config
      )
      .then((res) => {
        setChartData(res.data || []);
        setChartLoading(false);
        setMaeMfedetails(res.data);
      })
      .catch(() => {
        setError("Failed to load chart data");
        setChartLoading(false);
      });
  }, [
    symbol,
    startDate,
    endDate,
    timeFrame,
    idToken,
    requestPayload,
    setMaeMfedetails,
  ]);

  // Create and update chart
  useEffect(() => {
    if (
      !chartData?.candles?.length ||
      chartLoading ||
      !tradeData?.open_time ||
      !tradeData?.close_time
    )
      return;

    const container = chartContainerRef.current;
    if (!container) return;

    // Remove existing chart if it exists
    if (chartRef.current) {
      try {
        chartRef.current.remove();
      } catch (error) {
        // Ignore disposal errors
        console.log("Chart disposal error:", error);
      }
      chartRef.current = null;
    }

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      if (!container) return;

      const chart = createChart(container, {
        width: container.clientWidth,
        height: isFullScreen ? container.clientHeight : 500,
        layout: {
          background: { color: theme === "dark" ? "#131313" : "#fff" },
          textColor: theme === "dark" ? "#d1d4dc" : "#222",
        },
        grid: {
          vertLines: { visible: false },
          horzLines: { visible: false },
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          borderColor: theme === "dark" ? "#444" : "#ccc",
        },
        crosshair: { mode: 1 },
      });

      chartRef.current = chart;

      const candleSeries = chart.addCandlestickSeries({
        upColor: "#26a69a",
        downColor: "#ef5350",
        borderUpColor: "#26a69a",
        borderDownColor: "#ef5350",
        wickUpColor: "#26a69a",
        wickDownColor: "#ef5350",
      });

      const sortedData = [...chartData?.candles]
        .sort((a, b) => new Date(a.time) - new Date(b.time))
        .filter(
          (val, i, arr) =>
            i === 0 ||
            new Date(val.time).getTime() !== new Date(arr[i - 1].time).getTime()
        );

      const formattedData = sortedData.map((item) => ({
        // Keep original UTC timestamp for chart library
        time: Math.floor(new Date(item.time).getTime() / 1000),
        open: item.open,
        high: item.high,
        low: item.low,
        close: item.close,
      }));
      console.log(formattedData, "formattedData");

      candleSeries.setData(formattedData);

      // Tooltip functionality
      const handleMouseMove = (param) => {
        if (param.time && param.seriesData && param.seriesData.size > 0) {
          const candleData = param.seriesData.get(candleSeries);
          if (candleData) {
            const date = new Date(param.time * 1000);
            const formattedDate = formatDateInTimeZone(
              date,
              "M/D/YYYY, h:mm:ss A"
            );

            setTooltipData({
              time: formattedDate,
              open: candleData.open,
              high: candleData.high,
              low: candleData.low,
              close: candleData.close,
              change: (candleData.close - candleData.open).toFixed(5),
              changePercent: (
                ((candleData.close - candleData.open) / candleData.open) *
                100
              ).toFixed(2),
              isUp: candleData.close >= candleData.open,
            });

            setTooltipPosition({
              x: param.point.x,
              y: param.point.y,
            });
          }
        } else {
          setTooltipData(null);
        }
      };

      const handleMouseLeave = () => {
        setTooltipData(null);
      };

      chart.subscribeCrosshairMove(handleMouseMove);
      chart.subscribeClick(handleMouseLeave);

      // Trade line and markers
      const startTime = tradeData?.open_time
        ? Math.floor(new Date(tradeData.open_time).getTime() / 1000)
        : tradeData?.enteredAt
        ? Math.floor(new Date(tradeData.enteredAt).getTime() / 1000)
        : null;
      const endTime = tradeData?.close_time
        ? Math.floor(new Date(tradeData.close_time).getTime() / 1000)
        : tradeData?.exitedAt
        ? Math.floor(new Date(tradeData.exitedAt).getTime() / 1000)
        : null;
      const startPrice = tradeData?.open_price;
      const endPrice = tradeData?.close_price;

      if (startPrice && endPrice && startTime && endTime) {
        const trendLineSeries = chart.addLineSeries({
          color,
          lineWidth: 2,
          priceLineVisible: false,
          crosshairMarkerVisible: false,
          lineStyle: 0,
        });

        trendLineSeries.setData([
          { time: startTime, value: startPrice },
          { time: endTime, value: endPrice },
        ]);
        trendLineSeries.setMarkers([
          {
            time: startTime,
            position: "aboveBar",
            color: "#26a69a",
            shape: "arrowDown",
            size: 1,
            text: "Open",
          },
          {
            time: endTime,
            position: "belowBar",
            color: "#ef5350",
            shape: "arrowUp",
            size: 1,
            text: "Close",
          },
        ]);
      }

      // Fit content around trade period
      if (startTime && endTime) {
        chart.timeScale().fitContent();
        chart.timeScale().setVisibleRange({
          from: startTime - 10000,
          to: endTime + 10000,
        });
      }

      // Handle window resize for fullscreen
      const handleResize = () => {
        if (chartRef.current && container) {
          try {
            chartRef.current.applyOptions({
              width: container.clientWidth,
              height: isFullScreen ? container.clientHeight : 500,
            });
          } catch (error) {
            console.log("Chart resize error:", error);
          }
        }
      };

      window.addEventListener("resize", handleResize);

      // Store the handler for cleanup
      chart.resizeHandler = handleResize;
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (chartRef.current) {
        try {
          // Remove resize listener if it exists
          if (chartRef.current.resizeHandler) {
            window.removeEventListener(
              "resize",
              chartRef.current.resizeHandler
            );
          }
          chartRef.current.remove();
        } catch (error) {
          console.log("Chart disposal error:", error);
        }
        chartRef.current = null;
      }
    };
  }, [chartData, chartLoading, tradeData, color, isFullScreen, theme]); // theme added

  // Manage body scroll on fullscreen
  useEffect(() => {
    document.body.style.overflow = isFullScreen ? "hidden" : null;

    // Resize chart when fullscreen state changes
    if (chartRef.current && chartContainerRef.current) {
      setTimeout(() => {
        if (chartRef.current && chartContainerRef.current) {
          try {
            chartRef.current.applyOptions({
              width: chartContainerRef.current.clientWidth,
              height: isFullScreen
                ? chartContainerRef.current.clientHeight
                : 500,
            });
          } catch (error) {
            console.log("Chart resize error:", error);
          }
        }
      }, 100);
    }
  }, [isFullScreen]);

  // Tooltip content
  const tooltipContent = (
    <div style={{ maxWidth: "300px" }}>
      {/* MAE Definition */}
      <div style={{ marginBottom: "12px" }}>
        <div
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "#26a69a",
            marginBottom: "4px",
          }}
        >
          MAE = Maximum Adverse Excursion
        </div>
        <div
          style={{
            fontSize: "12px",
            color: theme === "dark" ? "#d1d4dc" : "#222",
            lineHeight: "1.4",
          }}
        >
          {tradeData?.mae
            ? `Max loss: $${tradeData.mae}`
            : "The level of loss an open position will fall into if it ends up in profit."}
        </div>
      </div>

      {/* MFE Definition */}
      <div style={{ marginBottom: "12px" }}>
        <div
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "#26a69a",
            marginBottom: "4px",
          }}
        >
          MFE = Maximum Favourable Excursion
        </div>
        <div
          style={{
            fontSize: "12px",
            color: theme === "dark" ? "#d1d4dc" : "#222",
            lineHeight: "1.4",
          }}
        >
          {tradeData?.mfe
            ? `Max profit: $${tradeData.mfe}`
            : "Shows the maximum possible profit on the position."}
        </div>
      </div>

      {/* Trade details if available */}
      {tradeData?.open_price && tradeData?.close_price && (
        <div
          style={{
            padding: "8px",
            backgroundColor: theme === "dark" ? "#2a2a2a" : "#f5f6fa",
            borderRadius: "4px",
            border: "1px solid " + (theme === "dark" ? "#444" : "#d8dadf"),
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: theme === "dark" ? "#d1d4dc" : "#222",
              lineHeight: "1.3",
            }}
          >
            <div>
              Open: ${tradeData.open_price} | Close: ${tradeData.close_price}
            </div>
            <div>
              PnL:{" "}
              <span
                style={{ color: tradeData.pnl >= 0 ? "#26a69a" : "#ef5350" }}
              >
                {FormatUSD(tradeData.pnl)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Candlestick tooltip component
  const CandlestickTooltip = () => {
    if (!tooltipData) return null;

    return (
      <div
        style={{
          position: "absolute",
          left: tooltipPosition.x + 10,
          top: tooltipPosition.y - 10,
          backgroundColor: theme === "dark" ? "#1a1a1a" : "#f5f6fa",
          border: "1px solid " + (theme === "dark" ? "#333" : "#d8dadf"),
          borderRadius: "8px",
          padding: "12px",
          color: theme === "dark" ? "#d1d4dc" : "#222",
          fontSize: "12px",
          zIndex: 1000,
          pointerEvents: "none",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
          minWidth: "200px",
        }}
      >
        <div
          style={{
            fontWeight: "600",
            marginBottom: "8px",
            color: tooltipData.isUp ? "#26a69a" : "#ef5350",
          }}
        >
          {tooltipData.time}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "4px",
          }}
        >
          <div>Open:</div>
          <div style={{ textAlign: "right" }}>${tooltipData.open}</div>
          <div>High:</div>
          <div style={{ textAlign: "right", color: "#26a69a" }}>
            ${tooltipData.high}
          </div>
          <div>Low:</div>
          <div style={{ textAlign: "right", color: "#ef5350" }}>
            ${tooltipData.low}
          </div>
          <div>Close:</div>
          <div style={{ textAlign: "right" }}>${tooltipData.close}</div>
          <div>Change:</div>
          <div
            style={{
              textAlign: "right",
              color: tooltipData.isUp ? "#26a69a" : "#ef5350",
              fontWeight: "600",
            }}
          >
            {tooltipData.isUp ? "+" : ""}
            {tooltipData.change} ({tooltipData.isUp ? "+" : ""}
            {tooltipData.changePercent}%)
          </div>
        </div>
      </div>
    );
  };

  // Header component
  const Header = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: theme === "dark" ? "#131313" : "#fff",
        color: theme === "dark" ? "#fff" : "#131313",
        padding: "12px 16px",
        borderTopLeftRadius: isFullScreen ? "0" : "12px",
        borderTopRightRadius: isFullScreen ? "0" : "12px",
        borderBottom: "1px solid " + (theme === "dark" ? "#333" : "#d8dadf"),
        position: isFullScreen ? "fixed" : "relative",
        top: isFullScreen ? 0 : "auto",
        left: isFullScreen ? 0 : "auto",
        right: isFullScreen ? 0 : "auto",
        zIndex: isFullScreen ? 10000 : "auto",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <div>
        <div
          style={{
            fontWeight: 600,
            fontSize: isFullScreen ? 20 : 17,
            color: theme === "dark" ? "#fff" : "#222",
            letterSpacing: 0.5,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {symbol || "-"}{" "}
          {tradeData?.direction ? `- ${tradeData.direction}` : ""}
        </div>
        <div
          style={{
            fontSize: isFullScreen ? 15 : 13,
            color: theme === "dark" ? "#fff" : "#222",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Open: {tradeData?.open_price}, Close: {tradeData?.close_price},
          Volume: {tradeData?.volume}, Duration: {tradeData?.duration}, PnL:{" "}
          <span style={{ color: getProfitColor(tradeData?.pnl) }}>
            ${tradeData?.pnl}
          </span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
        <div>
          <Select
            options={[
              { label: "1M", value: "one_minute" },
              { label: "5M", value: "five_minute" },
              { label: "15M", value: "fifteen_minute" },
              { label: "30M", value: "thirty_minute" },
              // { label: "1H", value: "one_hour" },
              // { label: "3H", value: "three_hour" },
              // { label: "6H", value: "six_hour" },
              // { label: "12H", value: "twelve_hour" },
            ]}
            value={timeFrame}
            className={`select-theme ${theme}`}
            popupClassName={`select-dropdown ${theme}`}
            style={{ minWidth: "60px" }}
            onChange={(value) => setTimeFrame(value)}
          />
        </div>
        <Tooltip
          title={`Switch to ${theme === "dark" ? "Light" : "Dark"} Mode`}
        >
          {theme === "dark" ? (
            <BulbOutlined
              style={{
                fontSize: isFullScreen ? 24 : 20,
                color: "#fff",
                cursor: "pointer",
              }}
              onClick={handleThemeToggle}
            />
          ) : (
            <BulbFilled
              style={{
                fontSize: isFullScreen ? 24 : 20,
                color: "#000",
                cursor: "pointer",
              }}
              onClick={handleThemeToggle}
            />
          )}
        </Tooltip>
        <Tooltip
          title={tooltipContent}
          placement="top"
          overlayStyle={{
            maxWidth: "350px",
          }}
          overlayInnerStyle={{
            backgroundColor: theme === "dark" ? "#1a1a1a" : "#fff",
            border: "1px solid " + (theme === "dark" ? "#333" : "#d8dadf"),
            borderRadius: "8px",
            padding: "16px",
            color: theme === "dark" ? "#d1d4dc" : "#222",
            fontFamily: "Inter', sans-serif",
          }}
        >
          <InfoCircleOutlined
            style={{
              fontSize: isFullScreen ? 24 : 20,
              color: theme === "dark" ? "#fff" : "#222",
              cursor: "pointer",
            }}
          />
        </Tooltip>
        {!isFullScreen ? (
          <FullscreenOutlined
            style={{
              fontSize: isFullScreen ? 24 : 20,
              color: theme === "dark" ? "#fff" : "#222",
              cursor: "pointer",
            }}
            onClick={() => setFullScreen(true)}
          />
        ) : (
          <FullscreenExitOutlined
            style={{
              fontSize: isFullScreen ? 24 : 20,
              color: theme === "dark" ? "#fff" : "#222",
              cursor: "pointer",
            }}
            onClick={() => setFullScreen(false)}
          />
        )}
      </div>
    </div>
  );

  // Fullscreen overlay
  if (isFullScreen) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: theme === "dark" ? "#131313" : "#fff",
          color: theme === "dark" ? "#fff" : "#222",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Header />
        <div
          style={{
            flex: 1,
            marginTop: "60px",
            position: "relative",
          }}
        >
          <div
            ref={chartContainerRef}
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
              background: theme === "dark" ? "#131313" : "#fff",
            }}
          />
          <CandlestickTooltip />
        </div>
        {error && (
          <div
            style={{
              color: "#ef5350",
              textAlign: "center",
              padding: "12px",
              backgroundColor: theme === "dark" ? "#131313" : "#fff",
            }}
          >
            {error}
          </div>
        )}
      </div>
    );
  }

  // Normal mode
  return (
    <Spin spinning={chartLoading}>
      <div
        style={{
          borderRadius: "12px",
          border: "1px solid " + (theme === "dark" ? "#272727" : "#d8dadf"),
          overflow: "hidden",
          fontFamily: "'Inter', sans-serif",
          background: theme === "dark" ? "#131313" : "#fff",
        }}
      >
        <Header />
        <div
          style={{
            backgroundColor: theme === "dark" ? "#131313" : "#fff",
            minHeight: "400px",
            position: "relative",
          }}
        >
          <div
            ref={chartContainerRef}
            style={{
              width: "100%",
              height: "500px",
              position: "relative",
              background: theme === "dark" ? "#131313" : "#fff",
            }}
          />
          <CandlestickTooltip />
        </div>
        {error && (
          <div
            style={{
              color: "#ef5350",
              textAlign: "center",
              marginTop: 12,
              padding: "12px",
              backgroundColor: theme === "dark" ? "#131313" : "#fff",
            }}
          >
            {error}
          </div>
        )}
      </div>
    </Spin>
  );
};

export default CandleStickChart;
